import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  StaffEditContainerStaffRoleFieldsFragment,
  StaffEditContainerStaffRolesDocument,
  StaffEditContainerStaffUserCurrentDocument,
  StaffEditContainerStaffUserCurrentFieldsFragment,
  StaffEditContainerStaffUserDocument,
  StaffEditContainerStaffUserFieldsFragment,
  StaffEditContainerStaffUserUpdateDocument,
  StaffUserUpdateInput
} from '../../../../generated';
import { useOid } from '../../../../hooks/useOid';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../shared/utils/queryOptions';
import { StaffEdit } from './staff-edit';

interface StaffEditContainerProps {}

export const StaffEditContainer: React.FC<StaffEditContainerProps> = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  const {
    data: dataEntity,
    loading,
    error
  } = useQuery(StaffEditContainerStaffUserDocument, {
    variables: {
      staffUserId: staffId
    }
  });

  const { data: listOfRoles, loading: listOfRolesLoading } = useQuery(StaffEditContainerStaffRolesDocument);
  const { data: currentStaffUser, loading: currentStaffUserLoading, error: currentStaffUserError } = useQuery(StaffEditContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);
  const [updateStaff, { loading: updateStaffLoading }] = useMutation(StaffEditContainerStaffUserUpdateDocument);

  const onSubmit = async (payload: StaffUserUpdateInput) => {
    try {
      const response = await updateStaff({
        variables: {
          input: { ...payload, id: staffId, role: payload.role?.id }
        }
      });
      if (response?.data?.staffUserUpdate?.status?.success) {
        message.success('Staff updated successfully.');
        navigate(-1);
      } else {
        message.error('Error saving staff user: ' + response?.data?.staffUserUpdate?.status?.errorMessage);
      }
    } catch (err: any) {
      message.error(`Failed to update the Staff: ${err?.message}`);
    }
  };

  return (
    <ComponentQueryLoader
      loading={loading || listOfRolesLoading || currentStaffUserLoading}
      hasData={dataEntity && currentStaffUser}
      error={error || currentStaffUserError}
      hasDataComponent={
        <StaffEdit
          updateStaffLoading={updateStaffLoading}
          onSubmit={onSubmit}
          currentStaffUser={currentStaffUser?.staffUserCurrent as StaffEditContainerStaffUserCurrentFieldsFragment}
          listOfRoles={listOfRoles?.staffRoles as StaffEditContainerStaffRoleFieldsFragment[]}
          data={dataEntity?.staffUser as StaffEditContainerStaffUserFieldsFragment}
        />
      }
    />
  );
};
